import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  ArrowLeft, UserPlus, Loader2, ShieldAlert, Clock, Package, X, Tag, Cpu,
} from "lucide-react";
import {
  IntentBadge, StatusBadge, SOURCE_LABEL, BU_LABEL, fullDateTime, timeHM,
} from "../../components/OpportunityBadges";

const TIMELINE_LABEL = {
  OPPORTUNITY_CREATED: "Oportunidade criada",
  OPPORTUNITY_DISPATCH: "Revendedora notificada",
  OPPORTUNITY_VIEWED: "Visualizada",
  OPPORTUNITY_CONTACT_STARTED: "Atendimento iniciado",
  OPPORTUNITY_CONVERTED: "Convertida",
  OPPORTUNITY_LOST: "Não convertida",
  OPPORTUNITY_CANCELLED: "Cancelada",
  OPPORTUNITY_ASSIGNED: "Revendedora atribuída",
  OPPORTUNITY_ASSIGN_REASON: "Motivo da atribuição registrado",
  BUSINESS_UNIT_RESOLVED: "Segmento corrigido",
};

function Modal({ open, title, icon: Icon, children, onClose, testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-t-3xl bg-white p-6 shadow-2xl sm:rounded-3xl">
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-brand-grad text-white">{Icon ? <Icon size={20} /> : null}</div>
          <div className="min-w-0 flex-1"><h3 className="text-lg font-bold text-brand-dark">{title}</h3><div className="mt-2 text-sm text-[#6B5A69]">{children}</div></div>
          <button onClick={onClose} aria-label="Fechar" data-testid={`${testid}-close`} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[#8A7A88] hover:bg-muted"><X size={18} /></button>
        </div>
      </div>
    </div>
  );
}

export default function AdminOpportunityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errStatus, setErrStatus] = useState(null);
  const [resellers, setResellers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [buOpen, setBuOpen] = useState(false);
  const [selReseller, setSelReseller] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [buVal, setBuVal] = useState("CRIS");
  const [buReason, setBuReason] = useState("");
  const [fbOpen, setFbOpen] = useState(false);
  const [fbRoute, setFbRoute] = useState("");
  const [fbLevel, setFbLevel] = useState("");
  const [fbNote, setFbNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErrStatus(null);
    try {
      const r = await api.get(`/opportunities/${id}`);
      setOpp(r.data);
    } catch (e) { setErrStatus(e?.response?.status || 500); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/whatsapp/resellers").then((r) => setResellers(r.data || [])).catch(() => {}); }, []);

  const doAssign = async () => {
    if (!selReseller) { toast.error("Selecione uma revendedora"); return; }
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/assign`, { reseller_id: selReseller, reason: reason.trim() || undefined, confirm });
      toast.success("Oportunidade atribuída");
      setAssignOpen(false); setReason(""); setConfirm(false); setSelReseller("");
      await load();
    } catch (e) {
      const st = e?.response?.status;
      const msg = e?.response?.data?.detail || "Falha ao atribuir";
      if (st === 409) { toast.warning(msg); setConfirm(true); } // exige confirmacao
      else toast.error(msg);
    } finally { setBusy(false); }
  };

  const doResolveBu = async () => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/business-unit`, { business_unit: buVal, reason: buReason.trim() || undefined });
      toast.success("Segmento atualizado");
      setBuOpen(false); setBuReason("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao corrigir segmento"); } finally { setBusy(false); }
  };

  const doFeedback = async () => {
    setBusy(true);
    try {
      await api.post(`/opportunities/${id}/classification-feedback`, {
        correct: false, expected_route: fbRoute || undefined,
        expected_intent_level: fbLevel || undefined, note: fbNote.trim() || undefined });
      toast.success("Feedback registrado (não altera a IA automaticamente)");
      setFbOpen(false); setFbRoute(""); setFbLevel(""); setFbNote("");
    } catch { toast.error("Falha ao registrar feedback"); } finally { setBusy(false); }
  };

  if (loading) return <div className="mx-auto max-w-3xl px-1 py-6" data-testid="admin-opp-detail-loading"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  if (errStatus) {
    const denied = errStatus === 403 || errStatus === 404;
    return (
      <div className="mx-auto max-w-3xl px-1 py-10 text-center" data-testid="admin-opp-detail-error">
        <ShieldAlert className="mx-auto text-red-500" size={32} />
        <p className="mt-3 text-base font-semibold text-brand-dark">{denied ? "Você não tem acesso a esta oportunidade." : "Erro ao carregar."}</p>
        <button onClick={() => nav("/admin/oportunidades")} className="mt-4 rounded-xl bg-brand-grad px-4 py-2 text-sm font-semibold text-white">Voltar</button>
      </div>
    );
  }

  const ai = opp.ai_metadata || {};
  const messages = (opp.messages || []).filter((m) => m.message_text);
  const timeline = (opp.timeline || []).filter((t) => TIMELINE_LABEL[t.action]);
  const resellerName = resellers.find((r) => r.id === opp.reseller_id)?.name;

  return (
    <div className="mx-auto max-w-3xl px-1 pb-16 sm:px-0" data-testid="admin-opp-detail-page">
      <button onClick={() => nav("/admin/oportunidades")} data-testid="admin-opp-detail-back" className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"><ArrowLeft size={16} /> Central de oportunidades</button>

      {/* Cabecalho */}
      <div className="rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-detail-header">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-bold text-brand-dark">{opp.customer_name || "Cliente sem nome"}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <IntentBadge level={opp.intent_level} />
              <StatusBadge status={opp.status} />
              {!opp.reseller_id && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-bold text-orange-700">SEM DONO</span>}
              {opp.customer_match_ambiguous && <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-bold text-purple-700">AMBÍGUO</span>}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Revendedora</div><div className="font-medium text-brand-dark">{resellerName || (opp.reseller_id ? opp.reseller_id : "— Sem responsável")}</div></div>
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Segmento</div><div className="font-medium text-brand-dark">{BU_LABEL[opp.business_unit] || opp.business_unit}</div></div>
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Origem</div><div className="font-medium text-brand-dark">{SOURCE_LABEL[opp.source] || opp.source}</div></div>
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Telefone</div><div className="font-medium text-brand-dark">{opp.customer_phone || "—"}</div></div>
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Recebida em</div><div className="font-medium text-brand-dark">{fullDateTime(opp.created_at)}</div></div>
          <div><div className="text-[11px] uppercase text-[#8A7A88]">Última mensagem</div><div className="font-medium text-brand-dark">{fullDateTime(opp.last_message_at)}</div></div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => { setSelReseller(opp.reseller_id || ""); setConfirm(false); setAssignOpen(true); }} data-testid="admin-opp-assign-btn"
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-grad px-4 py-2 text-sm font-semibold text-white"><UserPlus size={15} /> {opp.reseller_id ? "Reatribuir" : "Atribuir revendedora"}</button>
          {opp.business_unit === "UNKNOWN" && (
            <button onClick={() => setBuOpen(true)} data-testid="admin-opp-bu-btn"
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-brand-dark"><Tag size={15} /> Corrigir segmento</button>
          )}
        </div>
      </div>

      {/* Conversão (OP-7) */}
      {opp.status === "CONVERTED" && (
        <div className="mt-3 flex items-center justify-between rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-card" data-testid="admin-opp-converted">
          <div className="text-sm">
            <div className="font-bold text-emerald-800">Convertida</div>
            <div className="text-emerald-700" data-testid="admin-opp-conv-source">
              {opp.conversion_source === "SALE_LINK" ? "Conversão vinculada a venda" : "Conversão manual"}
            </div>
          </div>
          {opp.conversion_source === "SALE_LINK" && opp.converted_sale_id && (
            <button onClick={() => nav(opp.business_unit === "ELETROS" ? "/admin/eletros" : `/vendas/${opp.converted_sale_id}`)}
              data-testid="admin-opp-view-sale" className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">Ver venda</button>
          )}
        </div>
      )}

      {/* Classificacao IA */}
      <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-ai">
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-brand-dark"><Cpu size={15} className="text-brand" /> Classificação</h3>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-[#6B5A69]">Intenção: {opp.intent_level}</span>
          {opp.intent_type && <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-[#6B5A69]">{opp.intent_type}</span>}
          {opp.intent_confidence != null && <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-[#6B5A69]">Confiança: {Math.round(opp.intent_confidence * 100)}%</span>}
          {ai.classification_source && <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-[#6B5A69]">{ai.classification_source}</span>}
          {ai.model && <span className="rounded-full bg-muted px-2.5 py-1 font-medium text-[#6B5A69]">{ai.model}</span>}
        </div>
        <button onClick={() => setFbOpen(true)} data-testid="admin-opp-fb-btn"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100">
          <ShieldAlert size={13} /> Marcar classificação incorreta
        </button>
      </div>

      {/* Interesse + mensagem */}
      <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-interest">
        <h3 className="flex items-center gap-1.5 text-sm font-bold text-brand-dark"><Package size={15} className="text-brand" /> Interesse</h3>
        <p className="mt-2 text-sm text-[#4A3A48]">{(opp.product_text || "Não detalhado").trim()}</p>
        {opp.original_message && <blockquote className="mt-2 rounded-xl bg-muted px-3 py-2 text-sm italic text-[#4A3A48]">“{opp.original_message}”</blockquote>}
      </div>

      {messages.length > 0 && (
        <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-messages">
          <h3 className="text-sm font-bold text-brand-dark">Histórico da sessão</h3>
          <ul className="mt-2 space-y-1.5">
            {messages.map((m) => (
              <li key={m.id} className="flex gap-2 text-sm"><span className="shrink-0 tabular-nums text-[#8A7A88]">{timeHM(m.created_at)}</span><span className={m.direction === "out" ? "text-brand" : "text-[#4A3A48]"}>{m.direction === "out" ? "→ " : ""}{m.message_text}</span></li>
            ))}
          </ul>
        </div>
      )}

      {timeline.length > 0 && (
        <div className="mt-3 rounded-2xl border border-border bg-white p-5 shadow-card" data-testid="admin-opp-timeline">
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-brand-dark"><Clock size={15} className="text-brand" /> Linha do tempo e auditoria</h3>
          <ol className="mt-3 space-y-3">
            {timeline.map((t, i) => (
              <li key={t.id || i} className="flex gap-3"><div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" /><div><div className="text-sm font-medium text-brand-dark">{TIMELINE_LABEL[t.action]}</div><div className="text-[11px] text-[#8A7A88]">{fullDateTime(t.at)}{t.reason ? ` · ${t.reason}` : ""}</div></div></li>
            ))}
          </ol>
        </div>
      )}

      {/* Modal atribuir */}
      <Modal open={assignOpen} title={opp.reseller_id ? "Reatribuir oportunidade" : "Atribuir revendedora"} icon={UserPlus} onClose={() => setAssignOpen(false)} testid="admin-opp-assign-modal">
        <label className="mb-1 block text-xs font-semibold uppercase text-[#8A7A88]">Revendedora</label>
        <select value={selReseller} onChange={(e) => setSelReseller(e.target.value)} data-testid="admin-opp-assign-select" className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none">
          <option value="">Selecione…</option>
          {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        {(opp.reseller_id || opp.customer_id) && (
          <>
            <label className="mb-1 mt-3 block text-xs font-semibold uppercase text-[#8A7A88]">Motivo {opp.reseller_id ? "(obrigatório na reatribuição)" : ""}</label>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-opp-assign-reason" placeholder="Ex.: Cliente identificada posteriormente" className="w-full rounded-xl border border-border bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          </>
        )}
        {confirm && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="admin-opp-assign-confirm-note">
            Ação excepcional detectada. Ao confirmar novamente, prosseguiremos mesmo assim (auditado). Isso NÃO transfere a carteira da cliente.
          </div>
        )}
        <div className="mt-5 flex gap-3">
          <button onClick={() => setAssignOpen(false)} disabled={busy} className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button onClick={doAssign} disabled={busy} data-testid="admin-opp-assign-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-grad py-2.5 text-sm font-semibold text-white">{busy ? <Loader2 size={16} className="animate-spin" /> : null}{confirm ? "Confirmar mesmo assim" : "Atribuir"}</button>
        </div>
      </Modal>

      {/* Modal corrigir segmento */}
      <Modal open={buOpen} title="Corrigir segmento" icon={Tag} onClose={() => setBuOpen(false)} testid="admin-opp-bu-modal">
        <p>Defina o segmento correto desta oportunidade (ação auditada; não altera vendas existentes).</p>
        <div className="mt-3 flex gap-2">
          {["CRIS", "ELETROS"].map((u) => (
            <button key={u} onClick={() => setBuVal(u)} data-testid={`admin-opp-bu-${u}`} className={`flex-1 rounded-xl py-2.5 text-sm font-semibold ${buVal === u ? "bg-brand-grad text-white" : "border border-border bg-white text-brand-dark"}`}>{BU_LABEL[u]}</button>
          ))}
        </div>
        <input value={buReason} onChange={(e) => setBuReason(e.target.value)} data-testid="admin-opp-bu-reason" placeholder="Motivo (opcional)" className="mt-3 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none" />
        <div className="mt-5 flex gap-3">
          <button onClick={() => setBuOpen(false)} disabled={busy} className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button onClick={doResolveBu} disabled={busy} data-testid="admin-opp-bu-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-grad py-2.5 text-sm font-semibold text-white">{busy ? <Loader2 size={16} className="animate-spin" /> : null}Salvar</button>
        </div>
      </Modal>

      {/* Modal feedback de classificacao (falso positivo) */}
      <Modal open={fbOpen} title="Marcar classificação incorreta" icon={ShieldAlert} onClose={() => setFbOpen(false)} testid="admin-opp-fb-modal">
        <p>Registre a classificação correta esperada. Isso serve só para avaliação do piloto — <b>não</b> altera o prompt/lógica da IA automaticamente.</p>
        <label className="mb-1 mt-3 block text-xs font-semibold uppercase text-[#8A7A88]">Rota esperada</label>
        <select value={fbRoute} onChange={(e) => setFbRoute(e.target.value)} data-testid="admin-opp-fb-route" className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none">
          <option value="">— (manter)</option>
          {["COMMERCIAL_INTENT", "FINANCIAL_SUPPORT", "CUSTOMER_SUPPORT", "DELIVERY_SUPPORT", "GENERAL_MESSAGE"].map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <label className="mb-1 mt-3 block text-xs font-semibold uppercase text-[#8A7A88]">Nível esperado</label>
        <select value={fbLevel} onChange={(e) => setFbLevel(e.target.value)} data-testid="admin-opp-fb-level" className="w-full rounded-xl border border-border bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none">
          <option value="">— (manter)</option>
          {["HIGH", "MEDIUM", "LOW", "NONE"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input value={fbNote} onChange={(e) => setFbNote(e.target.value)} data-testid="admin-opp-fb-note" placeholder="Observação (opcional)" className="mt-3 w-full rounded-xl border border-border bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none" />
        <div className="mt-5 flex gap-3">
          <button onClick={() => setFbOpen(false)} disabled={busy} className="flex-1 rounded-xl border border-border bg-white py-2.5 text-sm font-semibold text-brand-dark hover:bg-muted">Voltar</button>
          <button onClick={doFeedback} disabled={busy} data-testid="admin-opp-fb-confirm" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-grad py-2.5 text-sm font-semibold text-white">{busy ? <Loader2 size={16} className="animate-spin" /> : null}Registrar</button>
        </div>
      </Modal>
    </div>
  );
}
